import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Github, Mic, PenTool, Smartphone, Wifi, Brain, BarChart3, BookOpen, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SprachMeister = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const features = [
    {
      icon: Mic,
      title: 'Sprechen (Speaking)',
      description: 'Practice speaking with AI-powered conversations. Real-time speech recognition with detailed metrics: WPM, filler words, grammar analysis.'
    },
    {
      icon: PenTool,
      title: 'Schreiben (Writing)',
      description: 'Writing practice with AI feedback. Grammar and style correction, vocabulary analysis, and progress tracking.'
    },
    {
      icon: Wifi,
      title: 'Offline First',
      description: 'Fully offline speech processing using on-device AI. Your voice never leaves your device.'
    },
    {
      icon: Brain,
      title: 'AI-Powered',
      description: 'Advanced language models provide natural conversations and accurate corrections for your German learning journey.'
    },
    {
      icon: BarChart3,
      title: 'Detailed Metrics',
      description: 'Track your progress with comprehensive analytics. See your improvement in real-time.'
    },
    {
      icon: BookOpen,
      title: 'Exam Preparation',
      description: 'Practice with Goethe B1 style exam scenarios to prepare for certification.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E9BF0]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1E9BF0]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Portfolio</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-[#1E9BF0]" />
            </div>
            <span className="font-semibold text-white">SprachMeister</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-32 h-32 rounded-3xl bg-white flex items-center justify-center mx-auto mb-6 shadow-lg shadow-black/10">
            <GraduationCap className="w-20 h-20 text-[#1E9BF0]" />
          </div>
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
            <Smartphone className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">iOS App</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            SprachMeister
          </h1>
          <p className="text-white/80 text-xl max-w-2xl mx-auto mb-8">
            AI-powered German language learning app. Practice speaking and writing with real-time feedback and detailed analytics.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://github.com/deeplearnproger/sprachmeister"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 transition-colors text-[#1E9BF0] px-6 py-3 rounded-full font-medium shadow-lg"
            >
              <Github className="w-5 h-5" />
              View on GitHub
            </a>
            <button
              onClick={() => navigate('/sprachmeister-privacy')}
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors text-white px-6 py-3 rounded-full font-medium"
            >
              <Shield className="w-5 h-5" />
              Privacy Policy
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg shadow-black/5">
                <div className="w-12 h-12 rounded-xl bg-[#1E9BF0] flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Built With</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {['Swift', 'SwiftUI', 'AVFoundation', 'Speech Framework', 'Accelerate', 'async/await'].map((tech, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-[#1E9BF0]/10 text-[#1E9BF0] rounded-full text-sm font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Requirements</h2>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#1E9BF0] mb-2">iOS 16+</div>
                <p className="text-gray-600 text-sm">Minimum iOS Version</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#1E9BF0] mb-2">Xcode 15+</div>
                <p className="text-gray-600 text-sm">Development Environment</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#1E9BF0] mb-2">Swift 5.9+</div>
                <p className="text-gray-600 text-sm">Programming Language</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6 text-center">
        <p className="text-white/60 text-sm">
          &copy; 2024 SprachMeister. All rights reserved.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <a
            href="https://github.com/deeplearnproger/sprachmeister"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            GitHub
          </a>
          <span className="text-white/30">·</span>
          <button
            onClick={() => navigate('/sprachmeister-privacy')}
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            Privacy Policy
          </button>
          <span className="text-white/30">·</span>
          <button
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            Portfolio
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SprachMeister;
